import { Injectable } from '@angular/core';
import { EventService } from './event.service';
import { Store } from '@ngrx/store';
import { deleteUndefinedEntriesInPlace, copyTitle } from '../helpers/helpers';
import { ProgramService } from './program.service';
import { Exhibitor, ExhibitorStand } from 'globals/store/exhibitors.data';
import { Ranking } from 'globals/store/events.data';
import { EntitiesService } from './entities.service';
import { EntityServiceFunctions } from 'globals/store/entity.data';
import { EntityBase } from 'globals/store/entity.shared.data';
import { createId, createSimpleId, jsonCopyObject } from 'globals/helpers/helpers';

// separating this out into its own service for better separation of concerns / clarity between services
@Injectable({ providedIn: 'root' })
export class ExhibitorsService implements EntityServiceFunctions {

  constructor(
    private programService: ProgramService,
    private eventService: EventService,
    public store: Store,
    private entitiesService: EntitiesService
  ) {}
  
  addExhibitorBase = (partialExhibitor:Partial<Exhibitor>): Exhibitor=> {
    const baseExhibitor: Exhibitor = {
      id: createSimpleId(),
      social: [],
      labels: [],
      name: ''
    } 
    return { ...baseExhibitor, ...partialExhibitor }
  }

  async addExhibitor(exhibitor: Exhibitor): Promise<Exhibitor> {
    const newExhibitor = this.addExhibitorBase(exhibitor)
    await this.eventService.saveEventPageEntities('exhibitors', [newExhibitor])
    return newExhibitor
  }

  async addMultipleExhibitors(newExhibitors: Partial<Exhibitor[]>): Promise<Exhibitor[]> {
    const exhibitors: Exhibitor[] = newExhibitors.map(this.addExhibitorBase);
    await this.eventService.saveEventPageEntities('exhibitors', exhibitors)
    return exhibitors;
  }

  async addExhibitorStand(stand: Partial<ExhibitorStand>) {
    const event = await this.eventService.getCurrentEvent()
    const id = createId()
    const { name, programId } = stand
    const newStand: ExhibitorStand = { 
      id,
      name: name || '',
      programId: programId || null, 
      programIndex: !programId ? null : Object.values(event.exhibitorStands).filter(s => s.programId === programId).length,
      assetIds: [],
      requests: [],
    }
    await this.eventService.saveEventPageEntities('exhibitorStands', [newStand])
    return newStand
  }

  async addMultipleExhibitorStands(newExhibitorStands: ExhibitorStand[]): Promise<ExhibitorStand[]> {
    await this.eventService.saveEventPageEntities('exhibitorStands', newExhibitorStands)
    return newExhibitorStands;
  }

  async deleteExhibitorStandsProgramId(standId: string) {
    const event = await this.eventService.getCurrentEvent()
    const stand = jsonCopyObject(event.exhibitorStands[standId])
    stand.programId = null
    await this.eventService.saveEventPageEntities('exhibitorStands', [stand])
  }

  async addOrderToExhibitorStands(exhibitorStandIds: string[], key: 'programIndex' | 'exhibitorIndex') { 
    const event = await this.eventService.getCurrentEvent();
    console.log({ exhibitorStandIds, key, event })
    const stands = exhibitorStandIds.map((id, index) => {
      let stand = event.exhibitorStands[id]
      console.log({ stand })
      if (!stand) return
      stand = jsonCopyObject(stand)
      stand[key] = index
      return stand
    }).filter(s => !!s)

    return await this.eventService.saveEventPageEntities('exhibitorStands', stands)
  }

  async clearIdsFromStands(exhibitorStandIds: string[], type: 'program' | 'exhibitor', typeId: string) {
    const event = await this.eventService.getCurrentEvent()
    // delete
    exhibitorStandIds.forEach(id => {

      const stand = event.exhibitorStands[id]
      if (!stand) return

      if (type === 'program') {
        event.exhibitorStands[id] = { ...event.exhibitorStands[id], programId: null, programIndex: null }
      } else {
        event.exhibitorStands[id] = { ...event.exhibitorStands[id], exhibitorId: null, exhibitorIndex: null }
      }
      return stand
    })

    Object.values(event.exhibitorStands)
      .filter(s => (type === 'program' ? s.programId : s.exhibitorId) === typeId)
      .forEach((s, index) => s[type === 'program' ? 'programIndex' : 'exhibitorIndex'] = index)
console.log({del: event.exhibitorStands})
    await this.eventService.saveEventPageEntities('exhibitorStands', Object.values(event.exhibitorStands))
  }


  async addExhibitorStands(exhibitorStandIds: string[], typeId: string, addToType: 'program' | 'exhibitor') {
    const event = await this.eventService.getCurrentEvent()
    const stands = Object.values(event.exhibitorStands).filter(s => 
      addToType === 'program' ? s.programId === typeId : 
      s.exhibitorId === typeId
    )
    console.log({ exhibitorStandIds, stands, event, typeId })
    const currentIndex = !stands.length ? 0 : Math.max(...stands.map(p => addToType === 'program' ? p.programIndex : p.exhibitorIndex)) + 1
    const newStands = exhibitorStandIds
      .map(id => event.exhibitorStands[id])
      .map((s, i) => { 
        if (addToType === 'program') {
          s.programId = typeId
          s.programIndex = currentIndex + i 
        } else {
          s.exhibitorId = typeId
          s.exhibitorIndex = currentIndex + i 
        }
        return s
      })
    await this.eventService.saveEventPageEntities('exhibitorStands', newStands)
  }

  async updateStandTypes(standTypes: Ranking[]) {
    const event = await this.eventService.getCurrentEvent()   
    standTypes.forEach((level, i) => {
      level.rank = standTypes.length - i
    })
    await this.eventService.updateEvent(event.eventId, { standTypes })
  }

  async updateExhibitors(updatedExhibitors: Exhibitor[]) {
    updatedExhibitors.forEach(deleteUndefinedEntriesInPlace)
    await this.eventService.saveEventPageEntities('exhibitors', updatedExhibitors)
  }

  async updateTypes(exhibitorTypes: Ranking[]) {
    exhibitorTypes.forEach((level, i) => level.rank = exhibitorTypes.length - i)
    const event = await this.eventService.getCurrentEvent()
    await this.eventService.updateEvent(event.eventId, { exhibitorTypes })
  }

  async updateEntities(entities: EntityBase[]) {
    await this.updateExhibitors(entities)
  }

  async updateExhibitorStands(exhibitorstands: ExhibitorStand[]) {
    exhibitorstands.forEach(deleteUndefinedEntriesInPlace)
    await this.eventService.saveEventPageEntities('exhibitorStands', exhibitorstands)
  }

  async updateExhibitor(exhibitor: Exhibitor) {
    await this.updateExhibitors([exhibitor])
  }

  async updateExhibitorStand(exhibitorStand: ExhibitorStand) {
    await this.updateExhibitorStands([exhibitorStand])
  }

  async deleteExhibitors(exhibitors: Exhibitor[]) {
    await this.eventService.deleteFromEventPages('exhibitors', exhibitors.map(e => e.id))
  }

  async deleteStandsFromEvent(exhibitorStandIds: string[]) {
    await this.eventService.deleteFromEventPages('exhibitorStands', exhibitorStandIds)
  }
  

  async deleteExhibitorStands(exhibitorStands: ExhibitorStand[]) {
    const exhibitorStandIds = exhibitorStands.map(s => s.id)
    await this.eventService.deleteFromEventPages('exhibitorStands', exhibitorStandIds)
  }

  async templateExhibitors(exhibitors: Exhibitor[]) {
    const exhibitorTemplates = await this.entitiesService.createEntityTemplates('exhibitor', exhibitors)
    const updatedExhibitors = exhibitors.map((s, i) => {
      const { dateAdded, dateModified, templateId, versionNumber } = exhibitorTemplates[i]
      const updatedExhibitor: Exhibitor = { ...s, dateAdded, dateModified, templateId, versionNumber }
      return updatedExhibitor
    })
    await this.updateExhibitors(updatedExhibitors)
  }

  async deleteExhibitor(exhibitor: Exhibitor) {
    await this.deleteExhibitors([exhibitor])
  }

  async deleteExhibitorStand(exhibitorStand: ExhibitorStand) {
    await this.deleteExhibitorStands([exhibitorStand])
  }

  async duplicateExhibitors(exhibitors: Exhibitor[]) {
    const newExhibitors = exhibitors.map(exhibitor => {
      deleteUndefinedEntriesInPlace(exhibitor)
      const newExhibitor = jsonCopyObject(exhibitor)
      newExhibitor.name = copyTitle(newExhibitor.name, Object.values(exhibitors).map(e => e.name))
      const id = createId()
      delete newExhibitor.confirmed
      delete newExhibitor.detailsConfirmed
      newExhibitor.id = id
      return newExhibitor
    })
    await this.eventService.saveEventPageEntities('exhibitors', newExhibitors)
  }

  async duplicateExhibitorStands(exhibitorStands: ExhibitorStand[]) {
    const event = await this.eventService.getCurrentEvent()
    const newStands = exhibitorStands.map(exhibitorStand => {
      deleteUndefinedEntriesInPlace(exhibitorStand)
      const newExhibitorStand = jsonCopyObject(exhibitorStand)
      newExhibitorStand.name = copyTitle(newExhibitorStand.name, Object.values(exhibitorStands).map(e => e.name))
      const id = createId()
      newExhibitorStand.id = id
      return newExhibitorStand
    })
    await this.eventService.saveEventPageEntities('exhibitorStands', newStands)
  }

  async duplicateExhibitor(exhibitor: Exhibitor) {
    await this.duplicateExhibitors([exhibitor])
  }

  async duplicateExhibitorStand(exhibitorStand: ExhibitorStand) {
    await this.duplicateExhibitorStands([exhibitorStand])
  }

  changeShowEditLinkInBrief(exhibitor: Exhibitor, includeEditUrl: boolean) {
    exhibitor = jsonCopyObject(exhibitor)
    exhibitor.showEditProfileLinkInBrief = includeEditUrl
    console.log({ exhibitor })
    this.updateExhibitor(exhibitor)
  }

}